import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { fetchCollection, fetchBySlug } from '../lib/api';
import { Helmet } from 'react-helmet-async';

export default function InfoPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    async function loadPage() {
      if (!slug) return;
      setLoading(true);
      setError(false);
      try {
        // Try fetchBySlug first (most efficient)
        let found = await fetchBySlug(slug, 'da');
        
        // Fallback to manual search in collections if fetchBySlug fails
        if (!found) {
          const collections = ['pages', 'news_items'];
          for (const col of collections) {
            const data = await fetchCollection(col);
            const items = Array.isArray(data) ? data : (data?.items || []);
            
            const match = items.find(p => {
              const pSlug = typeof p.slug === 'object' ? p.slug?.da : p.slug;
              return pSlug === slug;
            });
            if (match) {
              found = match;
              break;
            }
          }
        }
        
        if (found) {
          setPage(found);
        } else {
          setError(true);
        }
      } catch (err) {
        console.error('Error loading page:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    }
    loadPage();
  }, [slug]);

  if (loading) {
    return (
      <div className="pt-32 pb-24 flex justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#4b717a] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error || !page) {
    return (
      <main className="pt-32 pb-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl">
            <h1 className="text-4xl font-sans font-medium text-black mb-6 leading-tight tracking-tight">Siden blev ikke fundet</h1>
            <p className="text-slate-600 mb-8">Vi beklager, men den side du leder efter findes ikke eller er under opbygning.</p>
            <a href="/" className="inline-block bg-[#4b717a] text-white px-8 py-4 font-bold tracking-widest uppercase text-xs">Gå til forsiden</a>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="pt-32 pb-24">
      <Helmet>
        <title>{page.title.da || page.title} | Hellerup Lægehus</title>
      </Helmet>
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-12">
          <div className="flex items-center gap-4 mb-6">
            <div className="h-[1px] w-12 bg-[#4b717a]" />
            <span className="text-[#4b717a] text-xs tracking-[0.4em] uppercase font-bold">Information</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-sans font-medium text-black mb-8 leading-tight tracking-tight">
            {page.title.da || page.title}
          </h1>
        </div>

        <div 
          className="max-w-3xl prose prose-slate prose-lg
            prose-headings:font-sans prose-headings:font-medium prose-headings:text-black prose-headings:mt-12 prose-headings:mb-6 prose-headings:leading-tight prose-headings:tracking-tight
            prose-p:text-slate-600 prose-p:leading-relaxed
            prose-a:text-[#4b717a] prose-a:no-underline hover:prose-a:underline
            prose-strong:text-slate-900
            prose-ul:list-disc prose-ol:list-decimal"
          dangerouslySetInnerHTML={{ __html: page.content.da || page.content }}
        />
      </div>
    </main>
  );
}
